import React, { ElementType, ComponentProps } from "react";
import { PlusLink } from "@clarkinc/zest-design-system/ui";
import { cls } from "@wss/cls";
import { Image } from "@wss/image";
import ChildOrNull from "~/common/ChildOrNull";
import Stars from "~/common/Stars";
import { ProductDetailsCardDescription } from "./Description";
import { ProductDetailsCardPrice } from "./Price";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";
type RequiredAnchorProps = "href" | "title";
type AnchorProps = Omit<ComponentProps<"a">, RequiredAnchorProps> & {
  href: string;
  title: string;
};
type BaseCardProps<E extends ElementType> = {
  as?: E;
  anchorProps: AnchorProps;
  imgProps: Partial<ComponentProps<typeof Image>>;
  starsProps: ComponentProps<typeof Stars>;
  plusProps?: ComponentProps<typeof PlusLink>;
  descriptionProps: ComponentProps<typeof ProductDetailsCardDescription>;
  saleBadgeAndPricing: ComponentProps<typeof SaleBadgeAndPricing>;
  priceProps: ComponentProps<typeof ProductDetailsCardPrice>;
  withPlusLink?: boolean;
  withSalePrice?: boolean;
};
type StarsCardProps<E extends ElementType> = Omit<ComponentProps<E>, keyof BaseCardProps<E>> & BaseCardProps<E>;
export function StarsCard<E extends ElementType = "div">({
  as,
  anchorProps,
  className,
  imgProps,
  starsProps,
  descriptionProps,
  saleBadgeAndPricing,
  priceProps,
  plusProps,
  withPlusLink,
  withSalePrice,
  ...rest
}: StarsCardProps<E>) {
  const Component = as ?? "div";
  return <Component {...rest} className={cls("group flex flex-wrap justify-center relative", className)} data-sentry-element="Component" data-sentry-component="StarsCard" data-sentry-source-file="StarsCard.tsx">
      <a {...anchorProps} title={anchorProps.title} className={cls("absolute top-0 left-0 w-full h-full focus:outline-none focus:ring ring-inset ring-blue-700", anchorProps?.className)} />
      <Image width={1} height={1} {...imgProps} className={cls("block w-4/5 mx-auto h-min p-1 group-hover:border-gray-200 border border-solid border-transparent mb-1.5", imgProps.className)} data-sentry-element="Image" data-sentry-source-file="StarsCard.tsx" />
      <ChildOrNull condition={!!withPlusLink} data-sentry-element="ChildOrNull" data-sentry-source-file="StarsCard.tsx">
        <PlusLink {...plusProps} className={cls("inline-flex mx-1 align-middle float-left z-5", plusProps?.className)} data-sentry-element="PlusLink" data-sentry-source-file="StarsCard.tsx" />
      </ChildOrNull>
      <Stars {...starsProps} data-sentry-element="Stars" data-sentry-source-file="StarsCard.tsx" />
      <ProductDetailsCardDescription {...descriptionProps} data-sentry-element="ProductDetailsCardDescription" data-sentry-source-file="StarsCard.tsx" />
      <ChildOrNull condition={!!withSalePrice} data-sentry-element="ChildOrNull" data-sentry-source-file="StarsCard.tsx">
        <SaleBadgeAndPricing salePricingClassNames={cls("m-0 text-[0.7em]", saleBadgeAndPricing.salePricingClassNames)} saleBadgeExtraClassNames={cls("mr-1 mt-0.5 px-3/4 py-[1px]", saleBadgeAndPricing.saleBadgeExtraClassNames)} {...saleBadgeAndPricing} data-sentry-element="SaleBadgeAndPricing" data-sentry-source-file="StarsCard.tsx" />
      </ChildOrNull>
      <ProductDetailsCardPrice {...priceProps} data-sentry-element="ProductDetailsCardPrice" data-sentry-source-file="StarsCard.tsx" />
    </Component>;
}
StarsCard.Image = Image;
StarsCard.PlusLink = PlusLink;
StarsCard.Stars = Stars;
StarsCard.Description = ProductDetailsCardDescription;
StarsCard.SaleBadgeAndPricing = SaleBadgeAndPricing;
StarsCard.Price = ProductDetailsCardPrice;